import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { Image } from "@/components/Image/Image";
import { PaymentDetails, PaymentDetailsRow } from "@/components/PaymentDetails";
import { TermsAndConditionsAlert } from "@/components/TermsAndConditionsAlert/TermsAndConditionsAlert";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { formatCurrency } from "@/utils/currency";

type Props = {
  basePrice: number;
  brandedFarePrice?: number;
  promoCodeDiscount?: number;
  servicesPrice?: number;
  totalPrice: number;
};

export const PaymentSummaryCard = ({
  basePrice,
  brandedFarePrice,
  promoCodeDiscount,
  servicesPrice,
  totalPrice,
}: Props) => {
  const intl = useIntl();
  const { selectedInstallments, paymentType } = usePaymentContext();

  const nextMonth = dayjs().add(1, "month");

  return (
    <PaymentDetails
      title={intl.formatMessage({
        defaultMessage: "Payment Summary",
        description: "Title for the payment summary section",
      })}
      totalPrice={formatCurrency(totalPrice)}
      footer={<TermsAndConditionsAlert />}
    >
      <PaymentDetailsRow
        label={intl.formatMessage({
          defaultMessage: "Base flight ticket",
          description:
            "Line item label for the base flight ticket price in the order summary",
        })}
        value={formatCurrency(basePrice)}
      />

      {brandedFarePrice ? (
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Branded fare",
            description:
              "Line item label for the branded fare price in the order summary",
          })}
          value={formatCurrency(brandedFarePrice)}
        />
      ) : null}

      {servicesPrice ? (
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Selected services",
            description:
              "Line item label for the selected services in the price summary",
          })}
          value={formatCurrency(servicesPrice)}
        />
      ) : null}

      {promoCodeDiscount ? (
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Promo code discount",
            description:
              "Line item label for the promo code discount in the price summary",
          })}
          value={formatCurrency(promoCodeDiscount, undefined, {
            prefix: "-",
          })}
          color="accent"
        />
      ) : null}

      {paymentType === "bnpl" ? (
        <>
          <PaymentDetailsRow
            label={intl.formatMessage({
              defaultMessage: "Next payment",
              description:
                "Label for the date of the next scheduled installment payment",
            })}
            value={intl.formatDate(nextMonth.toDate(), {
              year: "numeric",
              month: "short",
            })}
          />

          <PaymentDetailsRow
            label={intl.formatMessage({
              defaultMessage: "Number of installments",
              description:
                "Label for the number of installments in the payment plan",
            })}
            value={intl.formatMessage(
              {
                defaultMessage: "{count} months",
                description: "Number of months for the installment plan",
              },
              { count: selectedInstallments },
            )}
          />
        </>
      ) : (
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Payment method",
            description:
              "Label for the selected payment method shown in the booking summary",
          })}
          value={
            <span className="inline-flex items-center gap-2">
              {intl.formatMessage({
                defaultMessage: "Qi Card",
                description: "Payment method label for the QI card",
              })}
              <Image
                src="/qicardlogo.png"
                alt=""
                aria-hidden
                className="shrink-0"
              />
            </span>
          }
        />
      )}
    </PaymentDetails>
  );
};
